import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { MenuService } from '../menu/menu.service';
import { OrderService } from '../services/order.service';
import { CtaService } from '../services/cta.service';
import { DataService } from '../services/data.service';
import { CartLineView, GetOrder } from '../models/cart.model';
import { OrderStatus, PaymentMode } from '../shared/enums';
import { CartService } from '../services/cart.service';
import { toCartLineViewFromOrderItem } from '../cart/cart.utils';

const STATUS_CONTENT: Record<OrderStatus, { icon: string; message: string }> = {
  [OrderStatus.Paid]: { icon: 'order_confirmed.svg', message: 'ORDER.PLACED' },
  [OrderStatus.Partial]: { icon: 'order_confirmed.svg', message: 'ORDER.PLACED' },
  [OrderStatus.Unpaid]: { icon: 'payment_failed.svg', message: 'ORDER.UNPAID' },
  [OrderStatus.Cancelled]: { icon: 'payment_failed.svg', message: 'ORDER.CANCELLED' },
  [OrderStatus.Void]: { icon: 'payment_failed.svg', message: 'ORDER.VOID' },
};

@Component({
  selector: 'app-order-confirmation',
  templateUrl: './order-confirmation.component.html',
  styleUrls: ['./order-confirmation.component.scss'],
  standalone: false
})
export class OrderConfirmationComponent {

  public order: GetOrder;
  public lines: CartLineView[];
  public showReceiptButton: boolean;
  public statusIcon: string;
  public statusMessage: string;
  public isPaid = false;

  private closed = false;
  private ctaService = inject(CtaService);
  private dataService = inject(DataService);
  private menuService = inject(MenuService);

  public readonly orderMessage = computed(() => this.menuService.menu()?.order_message);
  private cartService = inject(CartService);

  constructor(
    private orderService: OrderService,
    private router: Router,
  ) {
    this.order = this.orderService.getLastOrder();
    if (!this.order) {
      this.close();
      return;
    }
    this.lines = this.order.items.map(toCartLineViewFromOrderItem);
    this.isPaid = this.order.status == OrderStatus.Paid;

    const paymentMode = this.dataService.restaurant()?.payment_mode;
    const isUnpaidButNotPrepaid = this.order.status === OrderStatus.Unpaid
      && paymentMode !== PaymentMode.Prepaid;
    const statusForContent = isUnpaidButNotPrepaid ? OrderStatus.Paid : this.order.status;

    const { icon, message } = STATUS_CONTENT[statusForContent] ?? STATUS_CONTENT[OrderStatus.Void];
    this.statusIcon = icon;
    this.statusMessage = message;

    const isSuccessful = statusForContent === OrderStatus.Paid
      || this.order.status === OrderStatus.Partial;
    if (isSuccessful) {
      this.cartService.clearCart();
    }

    this.showReceiptButton = this.order.payment_type.is_online
      && this.order.total_price > 0;

    this.initializeCTA();
  }

  public initializeCTA(): void {
    this.ctaService.cta.set({
      url: this.order.cta.url,
      text: this.order.cta.text,
      total_price: this.order.total_price,
    });
  }

  public close(): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.router.navigate(['']);
  }

  public openReceipt(): void {
    const url = `${environment.urls.api}/receipt/order/${this.order.order_id}.pdf`;
    window.open(url, '_blank');
  }


  public openCTA(): void {
    this.ctaService.openCTA();
  }
}
